import { Spacer, VDBText } from "@components/atoms";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import Routes from "@navigations/Routes";
import { useIsFocused, useLinkTo } from "@react-navigation/native";
import {
  setProductType,
  setSelectedProduct,
} from "@redux/slices/appStateSlice";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Linking, Platform, Pressable, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import styled, { useTheme } from "styled-components/native";
import { ProductTypeProps } from "../SavedSearchTab/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { VDBIcons } from "@utils/VDBIcons";
import { CoachOverlayStep } from "@components/CoachOverlay";
import { useOverlayConfig } from "@redux/slices/globalSlice";
import { View as ShadowView } from "@gluestack-ui/themed";
import useProductArray from "@components/common/hooks/useProductArray";
import {
  setCollection,
  setFiltersPayload,
  setPayloadData,
  setSectionName,
  setDisplayTabs,
  setCollectionTabs,
} from "@redux/slices/searchFilters";
import { css } from "styled-components/native";
import { navigate } from "@navigations/utils/RootNavigation";
import { getProductThemeFromType } from "@components/common/Header/utils";
import {
  getBoxShadow,
  replaceDeeplinkScheme,
} from "@utils/product/productValidators";
import { getProductColor } from "@utils/product/productTheme";
import Animated from "react-native-reanimated";
import useProductTypesScroll from "./useProductTypesScroll";
import useHideNavsOnScroll from "@navigations/utils/useHideNavsOnScroll";

const ProductTypes = ({}: ProductTypeProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();

  const dispatch = useDispatch();
  const getOverlayConfig = useOverlayConfig();
  const overlayActiveScreens = useSelector(
    (state) => state.appState.overlayActiveScreens
  );
  const { productArray } = useProductArray();
  const isFocused = useIsFocused();
  const { onScroll: customProductScroll } = useProductTypesScroll();
  const { scrollHandler } = useHideNavsOnScroll();
  const onScrollHandler = scrollHandler();
  const onScroll = isFocused
    ? Platform.OS === "android"
      ? customProductScroll
      : onScrollHandler
    : undefined;
  const linkTo = useLinkTo();

  const navigateToType = (item: any) => {
    dispatch(setProductType(item?.productType));
    dispatch(setSelectedProduct(getProductColor(item?.productType)));
    if (theme.isLargeScreen) {
      dispatch(setSectionName(item.sectionName));
    }

    if (item?.deeplink) {
      let url = replaceDeeplinkScheme(
        item?.deeplink,
        process.env.EXPO_PUBLIC_APP_SCHEME
      );
      if (Platform.OS === "web") {
        linkTo(url);
      } else {
        Linking.openURL(url);
      }
    } else {
      const params = {
        productType: item?.productType,
        fromResultScreen: false,
        sectionName: item.sectionName,
        productPrefix: item.productPrefix ?? item?.productType,
      };
      // @ts-ignore
      if (item.sectionName) params.sectionName = item.sectionName;
      // @ts-ignore
      navigate(
        item.skipFilterScreenForWeb && !theme.isMobile
          ? Routes.FilterSplitScreen
          : Routes.FilterScreen,
        params
      );
    }
  };

  const ItemCard = ({ item }: any) => {
    const productTheme = getProductThemeFromType(item.productType);

    const handleOptionClick = (option: any, event: any) => {
      event.stopPropagation();

      let payload = {};
      if (item.sectionName && option.predefined_payload) {
        payload = option.predefined_payload;

        if (option?.collection) {
          dispatch(setCollection(option.collection));
        }

        const sectionName = option?.sectionName || item?.sectionName;
        dispatch(
          setFiltersPayload({
            sectionName: sectionName,
            data: payload,
          })
        );

        dispatch(
          setPayloadData({
            data: { [sectionName]: payload },
            productType: option.productType || item.productType,
          })
        );

        dispatch(setProductType(option.productType || item.productType));
        dispatch(setSectionName(sectionName));
        dispatch(
          setSelectedProduct(
            getProductColor(option.productType || item.productType)
          )
        );

        navigate(Routes.ExploreNavigator, {
          screen: theme.isMobile
            ? Routes.SearchResultScreen
            : Routes.FilterSplitScreen,
          params: {
            productType: option.productType || item.productType,
            sectionName: sectionName,
            filterSplitStep: 2,
            fromResultScreen: true,
            productPrefix: option.productPrefix || item.productPrefix,
            collection: option?.collection,
            skipFilterScreenForWeb: true,
          },
        });
      } else {
        navigateToType({
          productType: option.productType || item.productType,
          sectionName: option.sectionName || item.sectionName,
          productPrefix: option.productPrefix || item.productPrefix,
          title: option.label || option.name,
          icon: option.icon?.data || item.icon,
          productColor: option.icon?.icon_color || item.productColor,
          ...option,
          id: item.id,
        });
      }
    };

    const handleMainCardClick = () => {
      if (item.displaySubmenu && item.options?.length > 0) {
        return;
      }

      if (item.is_collection && item.options?.length > 0) {
        if (item.displayTabs) {
          dispatch(setCollectionTabs(item.options));
          dispatch(setDisplayTabs(true));

          const firstCollection = item.options[0];
          const collectionToUse =
            item.collection || firstCollection?.collection;

          if (collectionToUse) {
            dispatch(setCollection(collectionToUse));
          }

          const collectionSectionName = item.sectionName || item.productType;
          dispatch(setSectionName(collectionSectionName));

          if (
            firstCollection &&
            collectionSectionName &&
            firstCollection.predefined_payload
          ) {
            dispatch(
              setFiltersPayload({
                sectionName: collectionSectionName,
                data: firstCollection.predefined_payload,
              })
            );

            dispatch(
              setPayloadData({
                productType: item.productType,
                data: {
                  [collectionSectionName]: firstCollection.predefined_payload,
                },
              })
            );

            // dispatch(setFiltersSet());
          }

          navigate(Routes.SearchResultScreen, {
            productType: item.productType,
            sectionName: collectionSectionName,
            fromResultScreen: true,
            collection: collectionToUse,
            fromCollections: true,
            productPrefix: item.productPrefix ?? item?.productType,
            showCollectionTabs: true,
          });
        } else {
          // Original behavior for non-displayTabs collections
          navigate(Routes.CollectionsScreen, {
            productType: item.productType,
            options: item.options,
            title: item.title,
            sectionName: item.sectionName,
            productPrefix: item.productPrefix ?? item?.productType,
          });
        }
      } else {
        navigateToType(item);
      }
    };

    return (
      <Pressable
        onPress={handleMainCardClick}
        testID={`productTypes.mainCard.${item?.productType ?? "unknown"}`}
      >
        <CustomView>
          <CardParent style={{ width: "100%" }}>
            <ProductCard
              softShadow="1"
              displaySubmenu={item?.displaySubmenu && item?.options?.length > 0}
            >
              <CardMain>
                <VDBIcon
                  name={item.icon}
                  uri={item.icon}
                  size={40}
                  color={theme[item.productColor]}
                  variant={
                    item.type === IconsType.img_url
                      ? IconsVariant.CustomUrl
                      : IconsVariant.VDBCustom
                  }
                />
                <VDBText
                  color={
                    item.labelColor
                      ? theme[item.labelColor]
                      : theme[`${productTheme}-06`]
                  }
                  type={typography["H3-M"]}
                >
                  {item.title}
                </VDBText>
              </CardMain>

              <View>
                <VDBIcon
                  name={VDBIcons.ARROW_RIGHT}
                  size={24}
                  color={theme["core-04"]}
                  variant={IconsVariant.VDBCustom}
                />
              </View>
            </ProductCard>

            {item.displaySubmenu && item.options && item.options.length > 0 && (
              <SubmenuContainer>
                {item.options.map((option: any, index: number) => (
                  <Pressable
                    key={index}
                    onPress={(event) => handleOptionClick(option, event)}
                  >
                    <SubOptionCard isLast={index === item.options.length - 1}>
                      <SubOptionText
                        color={theme["core-06"]}
                        type={typography["Body-M"]}
                      >
                        {option?.label || option?.name}
                      </SubOptionText>
                      <VDBIcon
                        name={VDBIcons.ARROW_RIGHT}
                        size={24}
                        color={theme["core-04"]}
                        variant={IconsVariant.VDBCustom}
                      />
                      {index < item.options.length - 1 && <OptionSeparator />}
                    </SubOptionCard>
                  </Pressable>
                ))}
              </SubmenuContainer>
            )}
          </CardParent>
        </CustomView>
      </Pressable>
    );
  };

  return (
    <OverlayContainer bottom={overlayActiveScreens.ExploreScreen}>
      <CoachOverlayStep
        screen="ExploreScreen"
        stepKey="productTypes"
        text={
          getOverlayConfig.screens.ExploreScreen?.steps[2]?.text ||
          "Default product types overlay"
        }
        order={3}
        style={{
          justifyContent: "center",
          width: "100%",
          maxWidth: 608,
          ...(Platform.OS === "web" && !theme.isLargeScreen
            ? { height: "100%" }
            : {}),
        }}
      >
        <Animated.FlatList
          data={productArray}
          onScroll={onScroll}
          scrollEventThrottle={32}
          renderItem={({ item }: { item: any }) => <ItemCard item={item} />}
          keyExtractor={(item: any) => item.id.toString()}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            backgroundColor: theme["core-02"],
            paddingTop: SpaceType.Double,
            width: "100%",
            maxWidth: 608,
            paddingHorizontal: SpaceType.Double,
            paddingBottom: SpaceType.Size_80,
          }}
        />
        {Platform.OS === "android" && <Spacer size={120} />}
      </CoachOverlayStep>
    </OverlayContainer>
  );
};

const ProductCard = styled(ShadowView)<{ displaySubmenu?: boolean }>`
  background-color: ${({ theme }) => theme["core-01"]};
  width: 100%;
  flex-direction: row;
  align-items: center;
  padding: ${SpaceType.Double}px;
  border-radius: 8px;
  border-bottom-left-radius: ${(props) => (props.displaySubmenu ? "0" : "8px")};
  border-bottom-right-radius: ${(props) =>
    props.displaySubmenu ? "0" : "8px"};
  ${(props) =>
    props.displaySubmenu &&
    css`
      border-bottom-width: 1px;
      border-bottom-color: ${({ theme }) => theme["core-04"]};
    `}
`;

const OverlayContainer = styled.View<{ bottom: boolean }>`
  flex: 1;
  align-items: center;
  background-color: ${({ theme }) => theme["core-02"]};
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      padding: 10px;
    `}

  ${({ theme }) =>
    !theme.isLargeScreen &&
    Platform.OS === "web" &&
    css`
      overflow-y: auto;
    `}
`;

const CardMain = styled.View`
  flex-direction: row;
  align-items: center;
  align-content: flex-start;
  flex: 1;
  gap: 16px;
`;

const CardParent = styled.View`
  justify-content: center;
  align-items: center;
`;

const CustomView = styled.View`
  justify-content: center;
  align-items: center;
  margin-bottom: ${SpaceType.Double}px;
  ${({ theme }) =>
    css`
      box-shadow: ${getBoxShadow(theme.Shadows.Gentle)};
    `}
`;

const SubmenuContainer = styled.View`
  width: 100%;
  background-color: ${({ theme }) => theme["core-01"]};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
`;

const SubOptionCard = styled.View<{ isLast?: boolean }>`
  background-color: ${({ theme }) => theme["core-01"]};
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${SpaceType.Double}px;
  position: relative;
`;

const SubOptionText = styled(VDBText)`
  flex: 1;
`;

const OptionSeparator = styled.View`
  height: 1px;
  background-color: ${({ theme }) => theme["core-03"]};
  width: calc(100% - ${SpaceType.Double}px);
  position: absolute;
  bottom: 0;
`;

export default ProductTypes;
